/**
 * Contract-shaped fixtures.
 *
 * Every function here returns exactly what the endpoint in `docs/API.md`
 * returns — the same zod schemas from `@exam/shared` parse both. `transport.ts`
 * calls the real Worker first and only lands here when the route is still a
 * stub, so the parsing and assembly code in `client.ts` is identical in both
 * modes and there is no second code path to keep honest.
 *
 * Deleting this file (and the fallback branch in `transport.ts`) is all that
 * "switch to the real API" should ever require.
 *
 * Everything is deterministic — a seeded PRNG and a fixed `NOW` — so the
 * screens and the timeline look the same on every reload.
 */
import type {
  AdminAnswerDetail,
  AdminAttemptDetailResponse,
  AdminAttemptsResponse,
  AdminMediaResponse,
  AnswerResponse,
  Attempt,
  AttemptMedia,
  CorrectAnswer,
  Difficulty,
  ExamDetailResponse,
  ExamSummary,
  ExamsResponse,
  ProctorEvent,
  ProctorEventType,
  ProctorTimelineResponse,
  OverrideMarksInput,
  OverrideMarksResponse,
  PlaybackManifestResponse,
  QuestionSource,
  QuestionStatus,
  QuestionType,
  QuestionWithContext,
  Recording,
  ReviewQueueResponse,
  ReviewQuestionInput,
  Subject,
  SubjectsResponse,
  Topic,
  TopicsResponse,
  ViolationCounts,
} from "@exam/shared";
import {
  Difficulty as DifficultyEnum,
  QuestionSource as QuestionSourceEnum,
} from "@exam/shared";
import { OPTION_KEYS, PLAYBACK_TTL_S, PLAYBACK_WINDOW_DEFAULT } from "@exam/shared";
import { PROCTOR_EVENT_SEVERITY } from "@exam/shared";

/** Where fixture media is served from while testing the player locally. */
const MEDIA_BASE = (
  (import.meta.env.VITE_MOCK_MEDIA_BASE as string | undefined) ??
  "http://127.0.0.1:8899"
).replace(/\/$/, "");

/* ------------------------------------------------------------------ */
/* Deterministic helpers                                               */
/* ------------------------------------------------------------------ */

/** mulberry32 — small, fast, stable across reloads. */
function prng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rand = prng(20260722);

function pick<T>(items: readonly T[], r: number): T {
  return items[Math.min(items.length - 1, Math.floor(r * items.length))]!;
}

/** uuid-shaped so the contract's `Id` (`z.string().uuid()`) accepts it. */
function uid(namespace: string, n: number): string {
  let seed = (n * 2654435761) >>> 0;
  for (let i = 0; i < namespace.length; i++) {
    seed = (seed * 31 + namespace.charCodeAt(i)) >>> 0;
  }
  const hex = (len: number, s: number) => {
    const r = prng(s);
    let out = "";
    for (let i = 0; i < len; i++) out += Math.floor(r() * 16).toString(16);
    return out;
  };
  return `${hex(8, seed)}-${hex(4, seed + 1)}-4${hex(3, seed + 2)}-a${hex(3, seed + 3)}-${hex(12, seed + 4)}`;
}

const DAY = 86_400_000;
const MIN = 60_000;
/** Fixed "now" anchor so relative dates do not drift between reloads. */
const NOW = Date.UTC(2026, 6, 22, 6, 30, 0);

/* ------------------------------------------------------------------ */
/* Subjects & topics                                                   */
/* ------------------------------------------------------------------ */

const SUBJECT_DEFS = [
  { slug: "mathematics", name: "Mathematics" },
  { slug: "statistics", name: "Statistics" },
] as const;

/**
 * Topic names trimmed from content/syllabus/*.json — a real twelve-unit
 * syllabus, kept because the filters, the auto-pick buckets and the topic
 * breakdown all need a spread of units to sort and group by.
 */
const TOPIC_DEFS: Record<string, Array<{ week: number; name: string }>> = {
  mathematics: [
    { week: 1, name: "Set Theory" },
    { week: 1, name: "Relations and functions" },
    { week: 2, name: "Straight Lines" },
    { week: 3, name: "Quadratic Functions" },
    { week: 4, name: "Algebra of Polynomials" },
    { week: 4, name: "Graphs of Polynomials" },
    { week: 5, name: "Functions" },
    { week: 6, name: "Logarithmic functions" },
    { week: 7, name: "Sequences and limits" },
    { week: 8, name: "Derivatives" },
    { week: 9, name: "Integrals" },
    { week: 10, name: "Matrices and determinants" },
    { week: 11, name: "Systems of linear equations" },
    { week: 12, name: "Graph theory basics" },
  ],
  statistics: [
    { week: 1, name: "Types of data" },
    { week: 2, name: "Describing categorical data" },
    { week: 3, name: "Describing numerical data" },
    { week: 4, name: "Association between variables" },
    { week: 5, name: "Basic principles of counting" },
    { week: 6, name: "Probability" },
    { week: 7, name: "Conditional probability" },
    { week: 8, name: "Random variables" },
    { week: 9, name: "Expectation and variance" },
    { week: 10, name: "Binomial and Poisson" },
    { week: 11, name: "Continuous random variables" },
    { week: 12, name: "Normal distribution" },
  ],
};

const subjects: Subject[] = SUBJECT_DEFS.map((s, i) => ({
  id: uid("subject", i),
  name: s.name,
  slug: s.slug,
}));

const topics: Topic[] = SUBJECT_DEFS.flatMap((s, si) =>
  (TOPIC_DEFS[s.slug] ?? []).map((t, ti) => ({
    id: uid(`topic-${s.slug}`, ti),
    subject_id: subjects[si]!.id,
    name: t.name,
    week_no: t.week,
    video_url: null,
    parent_id: null,
  })),
);

const topicById = new Map(topics.map((t) => [t.id, t]));

/* ------------------------------------------------------------------ */
/* Questions                                                           */
/* ------------------------------------------------------------------ */

/**
 * Bodies deliberately carry LaTeX (`$…$`, `$$…$$`), markdown tables and code so
 * the review queue exercises the same renderer the student sees.
 */
const BODY_TEMPLATES: Array<{
  type: QuestionType;
  body: string;
  options?: string[];
  solution: string;
}> = [
  {
    type: "MCQ",
    body: "Let $f(x) = \\frac{x^2 - 9}{x - 3}$ for $x \\neq 3$.\n\nWhat value must $f(3)$ take for $f$ to be continuous at $x = 3$?",
    options: ["$0$", "$3$", "$6$", "$f$ cannot be made continuous"],
    solution:
      "Factor the numerator:\n\n$$\\frac{x^2-9}{x-3} = \\frac{(x-3)(x+3)}{x-3} = x + 3 \\quad (x \\neq 3)$$\n\nSo $\\lim_{x \\to 3} f(x) = 6$, and defining $f(3) = 6$ removes the discontinuity.",
  },
  {
    type: "MSQ",
    body: "Let $A = \\{1, 2, 3, 4\\}$ and $B = \\{3, 4, 5\\}$.\n\nWhich of the following statements are **true**?",
    options: [
      "$A \\cap B = \\{3, 4\\}$",
      "$|A \\cup B| = 5$",
      "$A \\setminus B = \\{1, 2\\}$",
      "$B \\subseteq A$",
    ],
    solution:
      "$A \\cap B = \\{3,4\\}$, $A \\cup B = \\{1,2,3,4,5\\}$ so $|A \\cup B| = 5$, and $A \\setminus B = \\{1,2\\}$.\n\n$B \\not\\subseteq A$ because $5 \\notin A$.",
  },
  {
    type: "NAT",
    body: "The line $L$ passes through $(2, -1)$ and is perpendicular to $3x + 4y = 12$.\n\nFind the $y$-intercept of $L$, correct to two decimal places.",
    solution:
      "The given line has slope $-\\frac{3}{4}$, so $L$ has slope $\\frac{4}{3}$.\n\n$$y + 1 = \\frac{4}{3}(x - 2) \\implies y = \\frac{4}{3}x - \\frac{11}{3}$$\n\nThe $y$-intercept is $-\\frac{11}{3} \\approx -3.67$.",
  },
  {
    type: "MCQ",
    body: "A fair six-sided die is rolled twice. Given that the sum is $8$, what is the probability that at least one roll shows a $6$?",
    options: [
      "$\\frac{1}{5}$",
      "$\\frac{2}{5}$",
      "$\\frac{1}{3}$",
      "$\\frac{1}{2}$",
    ],
    solution:
      "Outcomes summing to 8: $(2,6),(3,5),(4,4),(5,3),(6,2)$ — five of them.\n\nTwo contain a 6, so the probability is $\\frac{2}{5}$.",
  },
  {
    type: "NAT",
    body: "For the data below, compute the sample variance (denominator $n-1$).\n\n| Observation | Value |\n|---|---|\n| $x_1$ | 4 |\n| $x_2$ | 8 |\n| $x_3$ | 6 |\n| $x_4$ | 10 |\n\nGive the answer to two decimal places.",
    solution:
      "$\\bar{x} = 7$. Squared deviations: $9, 1, 1, 9$, summing to $20$.\n\n$$s^2 = \\frac{20}{4 - 1} = 6.67$$",
  },
  {
    type: "MCQ",
    body: "Which matrix below is the inverse of $\\begin{pmatrix} 2 & 1 \\\\ 5 & 3 \\end{pmatrix}$?",
    options: [
      "$\\begin{pmatrix} 3 & -1 \\\\ -5 & 2 \\end{pmatrix}$",
      "$\\begin{pmatrix} 2 & -1 \\\\ -5 & 3 \\end{pmatrix}$",
      "$\\begin{pmatrix} 3 & 1 \\\\ 5 & 2 \\end{pmatrix}$",
      "The matrix is singular",
    ],
    solution:
      "The determinant is $2 \\cdot 3 - 1 \\cdot 5 = 1$, so the inverse is the adjugate:\n\n$$\\begin{pmatrix} 3 & -1 \\\\ -5 & 2 \\end{pmatrix}$$",
  },
  {
    type: "MSQ",
    body: "Let $X \\sim \\text{Bin}(n = 10,\\ p = 0.3)$.\n\nSelect every correct statement.",
    options: [
      "$E[X] = 3$",
      "$\\operatorname{Var}(X) = 2.1$",
      "$P(X = 0) = 0.7^{10}$",
      "$X$ can take the value $10.5$",
    ],
    solution:
      "$E[X] = np = 3$, $\\operatorname{Var}(X) = np(1-p) = 2.1$, and $P(X=0) = (1-p)^{10} = 0.7^{10}$.\n\n$X$ is integer-valued, so $10.5$ is impossible.",
  },
  {
    type: "NAT",
    body: "Evaluate\n\n$$\\int_{0}^{2} (3x^2 - 4x + 1)\\, dx$$",
    solution: "$$\\left[ x^3 - 2x^2 + x \\right]_0^2 = (8 - 8 + 2) - 0 = 2$$",
  },
  {
    type: "MCQ",
    body: "The correlation coefficient between $X$ and $Y$ is $r = -0.85$. Which statement is best supported?",
    options: [
      "$X$ causes $Y$ to decrease",
      "There is a strong negative linear association",
      "There is no association",
      "The association is strong but non-linear",
    ],
    solution:
      "Correlation measures **linear** association only, and says nothing about causation. $|r| = 0.85$ is strong, and the sign is negative.",
  },
  {
    type: "MCQ",
    body: "A committee of 3 is chosen from 4 men and 3 women. What is the probability that it has at least one woman?\n\n*(Six options — papers are not always four-way.)*",
    options: [
      "$\\frac{1}{35}$",
      "$\\frac{4}{35}$",
      "$\\frac{18}{35}$",
      "$\\frac{31}{35}$",
      "$\\frac{34}{35}$",
      "$1$",
    ],
    solution:
      "The only all-male committee count is $\\binom{4}{3} = 4$ out of $\\binom{7}{3} = 35$.\n\n$$P(\\text{at least one woman}) = 1 - \\frac{4}{35} = \\frac{31}{35}$$",
  },
  {
    type: "MCQ",
    body: "How many distinct arrangements are there of the letters in `STATISTICS`?",
    options: [
      "$\\frac{10!}{3!\\,3!\\,2!}$",
      "$\\frac{10!}{3!\\,2!}$",
      "$10!$",
      "$\\frac{10!}{4!\\,3!}$",
    ],
    solution:
      "`STATISTICS` has 10 letters: S×3, T×3, I×2, A×1, C×1.\n\n$$\\frac{10!}{3!\\,3!\\,2!} = 50400$$",
  },
];

// Derived from the shared enums, never listed by hand: a fixture that omits a
// value is a fixture that cannot reproduce the bug that value causes.
const DIFFICULTIES: readonly Difficulty[] = DifficultyEnum.options;
const SOURCES: readonly QuestionSource[] = QuestionSourceEnum.options;

function correctAnswerFor(type: QuestionType, r: number): CorrectAnswer {
  if (type === "MCQ") return { type: "MCQ", key: pick(OPTION_KEYS.slice(0, 4), r) };
  if (type === "MSQ")
    return { type: "MSQ", keys: r > 0.5 ? ["A", "B", "C"] : ["A", "C"] };
  return { type: "NAT", value: Math.round(r * 2000) / 100 - 5, tolerance: 0.01 };
}

const questions: QuestionWithContext[] = (() => {
  const out: QuestionWithContext[] = [];
  let n = 0;
  for (const topic of topics) {
    const count = 3 + Math.floor(rand() * 4);
    const subject = subjects.find((s) => s.id === topic.subject_id)!;
    for (let i = 0; i < count; i++) {
      const tpl = BODY_TEMPLATES[n % BODY_TEMPLATES.length]!;
      const r = rand();
      // Roughly one in five stays pending — that is the review queue.
      const status: QuestionStatus =
        r < 0.2 ? "pending" : r < 0.9 ? "approved" : "rejected";
      out.push({
        id: uid("question", n),
        topic_id: topic.id,
        video_url: null,
        reading_url: null,
        starter_code: null,
        tests: null,
        type: tpl.type,
        body_md: tpl.body,
        options: (tpl.options ?? []).map((text, oi) => ({
          key: OPTION_KEYS[oi]!,
          text,
        })),
        correct_answer: correctAnswerFor(tpl.type, rand()),
        solution_md: tpl.solution,
        difficulty: pick(DIFFICULTIES, rand()),
        source: status === "pending" ? "generated" : pick(SOURCES, rand()),
        status,
        external_id: `${subject.slug}-gen-${String(n).padStart(3, "0")}`,
        marks: 1,
        confidence: status === "pending" ? pick(["high", "low"] as const, rand()) : null,
        source_url: null,
        // Imported NAT keys are truncated upstream, so some arrive unverified —
        // the bank and the review queue have to make that visible.
        key_verified: tpl.type === "NAT" ? rand() > 0.35 : true,
        import_source: status === "pending" ? "claude-generate" : `content-pack:${subject.slug}`,
        // One in twelve refers to a figure whose image never made it to R2.
        requires_figure: rand() > 0.92,
        figure_r2_key: null,
        figure_url: null,
        created_at: NOW - Math.floor(rand() * 40) * DAY,
        subject_id: subject.id,
        subject_name: subject.name,
        subject_slug: subject.slug,
        topic_name: topic.name,
        week_no: topic.week_no,
      });
      n++;
    }
  }
  return out;
})();

const questionById = new Map(questions.map((q) => [q.id, q]));

/* ------------------------------------------------------------------ */
/* Exams                                                               */
/* ------------------------------------------------------------------ */

interface ExamSeed {
  title: string;
  slug: string;
  weeks: number[];
  offsetDays: number;
  durationMin: number;
  count: number;
  status: ExamSummary["status"];
}

const EXAM_SEEDS: ExamSeed[] = [
  { title: "Mathematics — Quiz 1 (Units 1–4)", slug: "mathematics", weeks: [1, 2, 3, 4], offsetDays: -12, durationMin: 60, count: 10, status: "done" },
  { title: "Statistics — Quiz 1 (Units 1–4)", slug: "statistics", weeks: [1, 2, 3, 4], offsetDays: -9, durationMin: 60, count: 10, status: "done" },
  { title: "Mathematics — Quiz 2 (Units 5–8)", slug: "mathematics", weeks: [5, 6, 7, 8], offsetDays: -4, durationMin: 75, count: 12, status: "done" },
  { title: "Statistics — Quiz 2 (Units 5–8)", slug: "statistics", weeks: [5, 6, 7, 8], offsetDays: 2, durationMin: 75, count: 12, status: "scheduled" },
  { title: "Mathematics — Final (Units 1–12)", slug: "mathematics", weeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], offsetDays: 9, durationMin: 120, count: 20, status: "scheduled" },
  { title: "Statistics — Final (Units 1–12)", slug: "statistics", weeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], offsetDays: 0, durationMin: 120, count: 20, status: "draft" },
];

interface MockExam {
  exam: ExamSummary;
  questions: ExamDetailResponse["questions"];
}

const examStore: MockExam[] = EXAM_SEEDS.map((seed, i) => {
  const subject = subjects.find((s) => s.slug === seed.slug)!;
  const pool = questions.filter((q) => {
    const t = topicById.get(q.topic_id);
    return (
      t?.subject_id === subject.id &&
      q.status === "approved" &&
      t.week_no !== null &&
      seed.weeks.includes(t.week_no)
    );
  });

  const attached = pool.slice(0, seed.count).map((q, idx) => ({
    id: uid(`eq-${i}`, idx),
    exam_id: uid("exam", i),
    question_id: q.id,
    order_idx: idx,
    marks: q.difficulty === "hard" ? 4 : q.difficulty === "medium" ? 3 : 2,
    // The common default: a wrong answer costs marks only where there were
    // options to guess between, never on a free-entry numeric answer.
    negative_marks: q.type === "NAT" ? 0 : i % 2 === 0 ? 1 : 0.66,
    question: q,
  }));

  const scheduledAt = seed.status === "draft" ? null : NOW + seed.offsetDays * DAY;
  // A scheduled exam anchors to a fixed sitting with a window either side of it;
  // a from_start exam has no window and times the duration from the student's
  // own start. Both are fixtured so each mode has real data behind it.
  const scheduled = seed.status === "scheduled" && scheduledAt !== null;

  return {
    exam: {
      id: uid("exam", i),
      subject_id: subject.id,
      title: seed.title,
      timing_mode: scheduled ? "scheduled" : "from_start",
      scheduled_at: scheduledAt,
      available_from: scheduled ? scheduledAt - 10 * 60_000 : null,
      available_until: scheduled ? scheduledAt + 30 * 60_000 : null,
      duration_min: seed.durationMin,
      min_submit_after_min: 0,
      show_answers_after_submit: true,
      syllabus_visible: false,
      collect_answer_sheets: false,
      require_phone: false,
      allow_scratchpad: true,
      covers_weeks: seed.weeks,
      status: seed.status,
      created_at: NOW - (30 - i * 3) * DAY,
      question_count: attached.length,
      total_marks: attached.reduce((sum, a) => sum + a.marks, 0),
      attempt_status: seed.status === "done" ? "submitted" : null,
    },
    questions: attached,
  };
});

/* ------------------------------------------------------------------ */
/* Attempts                                                            */
/* ------------------------------------------------------------------ */

/**
 * A cohort, not a single student: the attempts list, the per-student links and
 * anything that groups by student are only exercised once more than one person
 * has sat the same paper.
 */
interface MockStudent {
  id: string;
  name: string;
}

const STUDENTS: MockStudent[] = [
  { id: uid("user", 1), name: "Amara Osei" },
  { id: uid("user", 2), name: "Ravi Menon" },
  { id: uid("user", 3), name: "Sofia Marchetti" },
  { id: uid("user", 4), name: "Jae-won Park" },
];

const EVENT_PLAN: Array<{
  at: number;
  type: ProctorEventType;
  metadata?: Record<string, unknown>;
}> = [
  { at: 0.04, type: "WINDOW_BLUR", metadata: { duration_ms: 2400 } },
  { at: 0.11, type: "NO_FACE", metadata: { duration_ms: 6200 } },
  { at: 0.19, type: "TAB_SWITCH", metadata: { away_ms: 14800, returned: true } },
  { at: 0.27, type: "NETWORK_DROP", metadata: { offline_ms: 3100 } },
  { at: 0.33, type: "COPY_ATTEMPT", metadata: { selection_len: 84 } },
  { at: 0.41, type: "PHONE_DISCONNECTED", metadata: { reconnected_after_ms: 61000 } },
  { at: 0.52, type: "MULTI_FACE", metadata: { faces: 2 } },
  { at: 0.58, type: "TAB_SWITCH", metadata: { away_ms: 9200, returned: true } },
  { at: 0.64, type: "WINDOW_BLUR", metadata: { duration_ms: 1800 } },
  { at: 0.71, type: "FULLSCREEN_EXIT" },
  { at: 0.78, type: "PASTE_ATTEMPT", metadata: { chars: 32 } },
  { at: 0.86, type: "NO_FACE", metadata: { duration_ms: 4400 } },
  { at: 0.93, type: "WEBCAM_STOPPED", metadata: { resumed_ms: 5000 } },
];

/** ~10 s chunks, as the media doc specifies. */
const CHUNK_MS = 10_000;

interface MockAttempt {
  attempt: Attempt;
  student_name: string;
  exam_title: string;
  subject_name: string;
  answers: AdminAnswerDetail[];
  events: Array<ProctorEvent & { offset_ms: number }>;
  recordings: Array<Recording & { offset_ms: number }>;
  photos: AttemptMedia[];
  duration_ms: number;
}

function responseFor(
  type: QuestionType,
  correct: CorrectAnswer,
  isCorrect: boolean,
  r: number,
): AnswerResponse | null {
  if (r < 0.08) return null; // left blank
  if (type === "MCQ") {
    const key = correct.type === "MCQ" ? correct.key : "A";
    if (isCorrect) return { type: "MCQ", key };
    return {
      type: "MCQ",
      key: pick(OPTION_KEYS.slice(0, 4).filter((k) => k !== key), r),
    };
  }
  if (type === "MSQ") {
    const keys = correct.type === "MSQ" ? correct.keys : ["A"];
    return { type: "MSQ", keys: isCorrect ? keys : keys.slice(0, 1) };
  }
  const value = correct.type === "NAT" ? correct.value : 0;
  return {
    type: "NAT",
    value: isCorrect ? value : Math.round((value + 1.5) * 100) / 100,
  };
}

function buildAttempt(
  source: MockExam,
  index: number,
  student: MockStudent,
): MockAttempt {
  const seed = 900 + index;
  const r = prng(seed);
  const exam = source.exam;
  const attemptId = uid("attempt", index);
  const startedAt = (exam.scheduled_at ?? NOW) + 2 * MIN;
  // Hardly anyone uses the full window, so vary the finish times.
  const durationMs = Math.round(exam.duration_min * MIN * (0.72 + r() * 0.24));
  const submittedAt = startedAt + durationMs;

  // Walk the paper front to back, then scatter a few revisits, so
  // `visit_offsets_ms` looks like a real navigation trail rather than a
  // monotonic sweep — the timeline's question markers depend on it.
  let cursor = 3_000;
  const answers: AdminAnswerDetail[] = source.questions.map((eq, i) => {
    const isCorrect = r() > 0.42;
    const response = responseFor(
      eq.question.type,
      eq.question.correct_answer,
      isCorrect,
      r(),
    );
    const answered = response !== null;
    const timeSpent = Math.round(
      (durationMs / source.questions.length) * (0.5 + r()),
    );

    const visits = [Math.min(cursor, durationMs - 2_000)];
    // Roughly a third of the paper gets a second look near the end.
    if (r() > 0.66) {
      visits.push(
        Math.min(
          durationMs - 1_000,
          Math.round(durationMs * (0.78 + r() * 0.18)),
        ),
      );
    }
    cursor += timeSpent;

    const auto = !answered ? 0 : isCorrect ? eq.marks : -eq.negative_marks;
    return {
      order_idx: i,
      marks: eq.marks,
      negative_marks: eq.negative_marks,
      question: eq.question,
      response,
      is_correct: answered ? isCorrect : false,
      marks_awarded: auto,
      auto_marks_awarded: auto,
      marks_override: null,
      override_note: null,
      time_spent_ms: timeSpent,
      visit_count: visits.length,
      visit_offsets_ms: visits.sort((a, b) => a - b),
    };
  });

  const events = EVENT_PLAN.filter(() => r() > 0.15).map((plan, i) => {
    const offset = Math.round(plan.at * durationMs);
    return {
      id: uid(`event-${seed}`, i),
      attempt_id: attemptId,
      ts_ms: startedAt + offset,
      type: plan.type,
      severity: PROCTOR_EVENT_SEVERITY[plan.type],
      metadata: plan.metadata ?? null,
      offset_ms: offset,
    };
  });

  // Chunk lists with deliberate holes: the phone drops out for ~1 minute around
  // its PHONE_DISCONNECTED event, and the webcam blinks once near the end.
  const total = Math.ceil(durationMs / CHUNK_MS);
  const gaps: Record<string, Set<number>> = {
    phone: new Set(
      Array.from({ length: 6 }, (_, k) => Math.floor(total * 0.41) + k),
    ),
    webcam: new Set([Math.floor(total * 0.93), Math.floor(total * 0.93) + 1]),
  };

  // The phone is paired, placed and used for the paper check and the room scan
  // before the exam clock starts, so it uploads a minute of footage at NEGATIVE
  // offsets — an observed −61 s in a real attempt. The webcam only starts with
  // the exam, so the setup window is phone-only. Both facts are fixtured here
  // because they are the two cases the player has to get right.
  const firstChunk: Record<string, number> = { webcam: 0, phone: -7 };

  const recordings: Array<Recording & { offset_ms: number }> = [];
  for (const src of ["webcam", "phone"] as const) {
    for (let i = firstChunk[src]!; i < total; i++) {
      if (gaps[src]!.has(i)) continue;
      const offset = i * CHUNK_MS;
      recordings.push({
        id: uid(`chunk-${src}-${seed}`, i + 16),
        attempt_id: attemptId,
        source: src,
        chunk_index: i,
      // The fixtures are one continuous recorder session.
      session_idx: 0,
        r2_key: `attempts/${attemptId}/rec/${src}/${i}.webm`,
        started_ms: startedAt + offset,
        duration_ms: Math.min(CHUNK_MS, durationMs - offset),
        bytes: 180_000 + Math.floor(prng(seed + i)() * 60_000),
        offset_ms: offset,
      });
    }
  }

  const photoPlan = [
    { kind: "room_scan" as const, at: -90_000 },
    { kind: "room_scan" as const, at: -75_000 },
    { kind: "paper_check" as const, at: -40_000 },
    { kind: "paper_check" as const, at: -30_000 },
    { kind: "answer_sheet" as const, at: durationMs + 20_000 },
    { kind: "answer_sheet" as const, at: durationMs + 40_000 },
    { kind: "answer_sheet" as const, at: durationMs + 60_000 },
  ];

  return {
    attempt: {
      id: attemptId,
      exam_id: exam.id,
      user_id: student.id,
      started_at: startedAt,
      submitted_at: submittedAt,
      score:
        Math.round(answers.reduce((s, a) => s + (a.marks_awarded ?? 0), 0) * 100) / 100,
      max_score: answers.reduce((s, a) => s + a.marks, 0),
      status: r() > 0.85 ? "auto_submitted" : "submitted",
      attempt_no: 1,
      extra_time_min: 0,
      rejoin_count: 0,
      offline_ms: 0,
    },
    student_name: student.name,
    exam_title: exam.title,
    subject_name: subjects.find((s) => s.id === exam.subject_id)?.name ?? "—",
    answers,
    events,
    recordings,
    photos: photoPlan.map((p, i) => ({
      id: uid(`media-${seed}`, i),
      attempt_id: attemptId,
      kind: p.kind,
      r2_key: `attempts/${attemptId}/photo/${p.kind}/${uid(`photo-${seed}`, i)}.jpg`,
      created_at: startedAt + p.at,
    })),
    duration_ms: durationMs,
  };
}

// Every finished exam is sat by the whole cohort, so each student has a
// history and every attempt still gets its own PRNG seed.
const attemptStore: MockAttempt[] = examStore
  .filter((e) => e.exam.status === "done")
  .flatMap((exam, ei) =>
    STUDENTS.map((student, si) =>
      buildAttempt(exam, ei * STUDENTS.length + si, student),
    ),
  );

/* ------------------------------------------------------------------ */
/* Mutable state — review actions and new exams persist for the session */
/* ------------------------------------------------------------------ */

function violationCounts(
  events: Array<{ type: ProctorEventType }>,
): ViolationCounts {
  const out: ViolationCounts = {};
  for (const t of Object.keys(PROCTOR_EVENT_SEVERITY) as ProctorEventType[]) {
    out[t] = 0;
  }
  for (const e of events) out[e.type] = (out[e.type] ?? 0) + 1;
  return out;
}

/* ------------------------------------------------------------------ */
/* Endpoint handlers — one per route in docs/API.md                    */
/* ------------------------------------------------------------------ */

export const mockEndpoints = {
  subjects(): SubjectsResponse {
    return { subjects };
  },

  topics(subjectId?: string): TopicsResponse {
    return {
      topics: subjectId
        ? topics.filter((t) => t.subject_id === subjectId)
        : topics,
    };
  },

  questions(query: Record<string, unknown>): {
    questions: QuestionWithContext[];
    total: number;
  } {
    const limit = Number(query.limit ?? 50);
    const offset = Number(query.offset ?? 0);
    const needle = String(query.q ?? "").toLowerCase();

    const matched = questions.filter((q) => {
      const topic = topicById.get(q.topic_id);
      if (query.topic_id && q.topic_id !== query.topic_id) return false;
      if (query.subject_id && topic?.subject_id !== query.subject_id) return false;
      if (query.week_no != null && topic?.week_no !== Number(query.week_no)) return false;
      if (query.type && q.type !== query.type) return false;
      if (query.difficulty && q.difficulty !== query.difficulty) return false;
      if (query.status && q.status !== query.status) return false;
      if (query.source && q.source !== query.source) return false;
      if (needle && !q.body_md.toLowerCase().includes(needle)) return false;
      return true;
    });

    return {
      questions: matched.slice(offset, offset + limit),
      total: matched.length,
    };
  },

  reviewQueue(query: Record<string, unknown>): ReviewQueueResponse {
    const pending = questions
      .filter((q) => q.status === "pending")
      .sort((a, b) => a.created_at - b.created_at);
    const limit = Number(query.limit ?? 50);
    const offset = Number(query.offset ?? 0);

    const rows = pending.map((question) => {
      const flags = {
        unverified_key: !question.key_verified,
        low_confidence: question.confidence === "low",
        missing_figure: question.requires_figure && question.figure_r2_key === null,
        numeric_key_may_be_truncated:
          question.type === "NAT" &&
          !question.key_verified &&
          question.import_source !== null &&
          question.import_source.startsWith("content-pack"),
      };
      const reasons: string[] = [];
      if (flags.unverified_key) reasons.push("Answer key not confirmed by a human");
      if (flags.numeric_key_may_be_truncated) {
        reasons.push("Numeric key may be truncated at the source");
      }
      if (flags.low_confidence) reasons.push("The source marked its answer uncertain");
      if (flags.missing_figure) reasons.push("Refers to a figure that is not stored");

      return {
        question,
        flags,
        needs_attention: Object.values(flags).some(Boolean),
        reasons,
      };
    });

    return {
      questions: rows.slice(offset, offset + limit),
      total: rows.length,
      summary: {
        pending: rows.length,
        unverified_key: rows.filter((r) => r.flags.unverified_key).length,
        missing_figure: rows.filter((r) => r.flags.missing_figure).length,
        low_confidence: rows.filter((r) => r.flags.low_confidence).length,
      },
    };
  },

  question(id: string): { question: QuestionWithContext } {
    const q = questionById.get(id);
    if (!q) throw new Error(`Unknown question ${id}`);
    return { question: q };
  },

  deleteFigure(id: string): { ok: true } {
    const q = questionById.get(id);
    if (!q) throw new Error(`Unknown question ${id}`);
    q.figure_r2_key = null;
    q.figure_url = null;
    // Same demotion the API performs — it still needs a figure nobody has.
    if (q.status === "approved") q.status = "pending";
    return { ok: true };
  },

  patchQuestion(id: string, patch: Record<string, unknown>): { question: QuestionWithContext } {
    const q = questionById.get(id);
    if (!q) throw new Error(`Unknown question ${id}`);
    Object.assign(q, patch);
    return { question: q };
  },

  /** Enforces the same two 409s the real endpoint does. */
  reviewQuestion(
    id: string,
    input: ReviewQuestionInput,
  ): { question: QuestionWithContext } {
    const q = questionById.get(id);
    if (!q) throw new Error(`Unknown question ${id}`);

    if (input.requires_figure !== undefined) q.requires_figure = input.requires_figure;
    if (input.figure_r2_key !== undefined) q.figure_r2_key = input.figure_r2_key;
    if (input.key_verified !== undefined) q.key_verified = input.key_verified;

    if (input.action === "approve") {
      if (q.type === "NAT" && !q.key_verified) {
        throw new Error(
          "This numeric answer key has not been confirmed. Check the value against the question and confirm it before approving.",
        );
      }
      if (q.requires_figure && q.figure_r2_key === null) {
        throw new Error(
          "This question refers to a figure that is not stored. Upload the figure or clear the figure requirement before approving.",
        );
      }
    }

    // `withdraw` returns it to the queue rather than binning it — the fixture
    // has to agree with the API or the screen behaves differently against
    // sample data than against the real thing.
    q.status =
      input.action === "approve"
        ? "approved"
        : input.action === "withdraw"
          ? "pending"
          : "rejected";
    return { question: q };
  },

  exams(query: Record<string, unknown>): ExamsResponse {
    const matched = examStore
      .map((e) => e.exam)
      .filter(
        (e) =>
          (!query.status || e.status === query.status) &&
          (!query.subject_id || e.subject_id === query.subject_id),
      )
      .sort((a, b) => (b.scheduled_at ?? 0) - (a.scheduled_at ?? 0));
    return { exams: matched, total: matched.length };
  },

  examDetail(id: string): ExamDetailResponse {
    const found = examStore.find((e) => e.exam.id === id);
    if (!found) throw new Error(`Unknown exam ${id}`);
    return { exam: found.exam, questions: found.questions };
  },

  createExam(body: Record<string, unknown>): { exam: ExamSummary } {
    const timingMode: ExamSummary["timing_mode"] =
      body.timing_mode === "scheduled" ? "scheduled" : "from_start";
    const exam: ExamSummary = {
      id: uid("exam", examStore.length + 500),
      subject_id: String(body.subject_id),
      title: String(body.title),
      timing_mode: timingMode,
      scheduled_at: (body.scheduled_at as number | null) ?? null,
      // The start window applies in both modes, as it does in the API.
      available_from: (body.available_from as number | null) ?? null,
      available_until: (body.available_until as number | null) ?? null,
      duration_min: Number(body.duration_min),
      min_submit_after_min: Number(body.min_submit_after_min ?? 0),
      show_answers_after_submit: body.show_answers_after_submit !== false,
      syllabus_visible: body.syllabus_visible === true,
      collect_answer_sheets: body.collect_answer_sheets === true,
      require_phone: body.require_phone === true,
      allow_scratchpad: body.allow_scratchpad !== false,
      covers_weeks: (body.covers_weeks as number[]) ?? [],
      status: "draft",
      created_at: Date.now(),
      question_count: 0,
      total_marks: 0,
      attempt_status: null,
    };
    examStore.unshift({ exam, questions: [] });
    return { exam };
  },

  attachQuestions(
    id: string,
    body: {
      questions: Array<{
        question_id: string;
        marks?: number;
        negative_marks?: number;
      }>;
      replace?: boolean;
    },
  ): ExamDetailResponse {
    const found = examStore.find((e) => e.exam.id === id);
    if (!found) throw new Error(`Unknown exam ${id}`);
    if (body.replace) found.questions = [];

    for (const item of body.questions) {
      const q = questionById.get(item.question_id);
      if (!q) continue;
      if (q.status !== "approved") {
        throw new Error("Only approved questions can be attached.");
      }
      if (found.questions.some((eq) => eq.question_id === q.id)) continue;
      found.questions.push({
        id: uid(`eq-new-${id}`, found.questions.length),
        exam_id: id,
        question_id: q.id,
        order_idx: found.questions.length,
        marks: item.marks ?? 1,
        negative_marks: item.negative_marks ?? 0,
        question: q,
      });
    }
    found.exam.question_count = found.questions.length;
    found.exam.total_marks = found.questions.reduce((s, q) => s + q.marks, 0);
    return { exam: found.exam, questions: found.questions };
  },

  publishExam(
    id: string,
    body: { scheduled_at?: number | null; status?: ExamSummary["status"] },
  ): { exam: ExamSummary } {
    const found = examStore.find((e) => e.exam.id === id);
    if (!found) throw new Error(`Unknown exam ${id}`);
    const next = body.status ?? "scheduled";
    if (body.scheduled_at != null) found.exam.scheduled_at = body.scheduled_at;
    if (next === "scheduled") {
      if (found.exam.scheduled_at == null) {
        throw new Error("A scheduled exam needs a date and time.");
      }
      if (found.questions.length === 0) {
        throw new Error("A scheduled exam needs at least one question.");
      }
    }
    found.exam.status = next;
    return { exam: found.exam };
  },

  /**
   * Mirrors the documented 40/40/20 rule closely enough to exercise the UI:
   * `weak` = topics with a wrong answer in a past attempt, `recent` = the top
   * 40% of covered weeks, `revision` = the rest.
   */
  autoPick(
    id: string,
    body: {
      count: number;
      marks?: number;
      negative_marks?: number;
      type?: QuestionType;
      difficulty?: Difficulty;
      dry_run?: boolean;
      replace?: boolean;
    },
  ) {
    const found = examStore.find((e) => e.exam.id === id);
    if (!found) throw new Error(`Unknown exam ${id}`);
    const exam = found.exam;

    const wrongByTopic = new Map<string, number>();
    for (const a of attemptStore) {
      for (const ans of a.answers) {
        if (ans.is_correct === false) {
          const t = ans.question.topic_id;
          wrongByTopic.set(t, (wrongByTopic.get(t) ?? 0) + 1);
        }
      }
    }

    const weeks = [...exam.covers_weeks].sort((a, b) => a - b);
    const recentCut = weeks[Math.floor(weeks.length * 0.6)] ?? 0;
    const already = new Set(
      body.replace ? [] : found.questions.map((q) => q.question_id),
    );

    const eligible = questions.filter((q) => {
      const t = topicById.get(q.topic_id);
      return (
        t?.subject_id === exam.subject_id &&
        q.status === "approved" &&
        !already.has(q.id) &&
        (weeks.length === 0 || (t.week_no !== null && weeks.includes(t.week_no))) &&
        (!body.type || q.type === body.type) &&
        (!body.difficulty || q.difficulty === body.difficulty)
      );
    });

    const bucketOf = (q: QuestionWithContext): "weak" | "recent" | "revision" => {
      if (wrongByTopic.has(q.topic_id)) return "weak";
      const week = topicById.get(q.topic_id)?.week_no ?? 0;
      return week >= recentCut ? "recent" : "revision";
    };

    const pools = {
      weak: [] as QuestionWithContext[],
      recent: [] as QuestionWithContext[],
      revision: [] as QuestionWithContext[],
    };
    for (const q of eligible) pools[bucketOf(q)].push(q);

    const targets = {
      weak: Math.round(body.count * 0.4),
      recent: Math.round(body.count * 0.4),
      revision: body.count - Math.round(body.count * 0.4) * 2,
    };

    const picked: Array<{
      question_id: string;
      bucket: "weak" | "recent" | "revision";
      topic_id: string;
      week_no: number | null;
      difficulty: Difficulty;
    }> = [];
    const shortfall: Record<string, number> = { weak: 0, recent: 0, revision: 0 };

    const order = ["weak", "recent", "revision"] as const;
    for (const bucket of order) {
      const want = targets[bucket];
      const take = pools[bucket].splice(0, want);
      shortfall[bucket] = want - take.length;
      for (const q of take) {
        picked.push({
          question_id: q.id,
          bucket,
          topic_id: q.topic_id,
          week_no: topicById.get(q.topic_id)?.week_no ?? null,
          difficulty: q.difficulty,
        });
      }
    }
    // Buckets that ran dry spill into whatever is left, in the same order.
    for (const bucket of order) {
      while (picked.length < body.count) {
        const q = pools[bucket].shift();
        if (!q) break;
        picked.push({
          question_id: q.id,
          bucket,
          topic_id: q.topic_id,
          week_no: topicById.get(q.topic_id)?.week_no ?? null,
          difficulty: q.difficulty,
        });
      }
    }

    if (!body.dry_run) {
      this.attachQuestions(id, {
        questions: picked.map((p) => ({
          question_id: p.question_id,
          marks: body.marks ?? 1,
          negative_marks: body.negative_marks ?? 0,
        })),
        replace: body.replace,
      });
    }

    return {
      dry_run: body.dry_run ?? false,
      requested: body.count,
      picked,
      shortfall,
    };
  },

  attempts(query: Record<string, unknown>): AdminAttemptsResponse {
    const rows = attemptStore
      .filter(
        (a) =>
          (!query.exam_id || a.attempt.exam_id === query.exam_id) &&
          (!query.status || a.attempt.status === query.status),
      )
      .sort((a, b) => (b.attempt.submitted_at ?? 0) - (a.attempt.submitted_at ?? 0))
      .map((a) => ({
        attempt: a.attempt,
        exam_title: a.exam_title,
        student_name: a.student_name,
        total_violations: a.events.length,
      }));
    return { attempts: rows, total: rows.length };
  },

  attemptDetail(id: string): AdminAttemptDetailResponse {
    const found = attemptStore.find((a) => a.attempt.id === id) ?? attemptStore[0];
    if (!found) throw new Error("No attempts");
    return {
      attempt: found.attempt,
      exam_title: found.exam_title,
      subject_name: found.subject_name,
      student_name: found.student_name,
      answers: found.answers,
      violation_counts: violationCounts(found.events),
      total_violations: found.events.length,
      duration_ms: found.duration_ms,
    };
  },

  attemptEvents(id: string): ProctorTimelineResponse {
    const found = attemptStore.find((a) => a.attempt.id === id) ?? attemptStore[0];
    if (!found) throw new Error("No attempts");
    return {
      attempt_id: found.attempt.id,
      events: found.events,
      violation_counts: violationCounts(found.events),
      total_violations: found.events.length,
    };
  },

  attemptMedia(id: string): AdminMediaResponse {
    const found = attemptStore.find((a) => a.attempt.id === id) ?? attemptStore[0];
    if (!found) throw new Error("No attempts");
    return {
      attempt_id: found.attempt.id,
      started_at: found.attempt.started_at,
      sources: (["webcam", "phone"] as const).map((source) => {
        const chunks = found.recordings.filter((c) => c.source === source);
        return {
          source,
          chunks,
          total_bytes: chunks.reduce((s, c) => s + c.bytes, 0),
          total_duration_ms: chunks.reduce((s, c) => s + c.duration_ms, 0),
        };
      }),
      photos: found.photos,
    };
  },

  /**
   * The windowed playback manifest. `from`/`limit` slice each source's chunks
   * `[from, from + limit)` (as the Worker does), and `total_*`/`has_more`/
   * `next_from` describe the whole attempt so the client can page. Both sources
   * share `offset_ms` origin = `attempts.started_at`.
   */
  playbackManifest(
    id: string,
    window: { from?: number; limit?: number } = {},
  ): PlaybackManifestResponse {
    const found = attemptStore.find((a) => a.attempt.id === id) ?? attemptStore[0];
    if (!found) throw new Error("No attempts");
    const from = window.from ?? 0;
    const limit = window.limit ?? PLAYBACK_WINDOW_DEFAULT;
    return {
      attempt_id: found.attempt.id,
      started_at: found.attempt.started_at,
      submitted_at: found.attempt.submitted_at,
      duration_ms: found.duration_ms,
      expires_at: Date.now() + PLAYBACK_TTL_S * 1000,
      window: { from, limit },
      sources: (["webcam", "phone"] as const).map((source) => {
        const all = found.recordings.filter((c) => c.source === source);
        const windowChunks = all.slice(from, from + limit);
        const nextFrom = from + limit;
        const hasMore = nextFrom < all.length;
        return {
          source,
          first_offset_ms: all[0]?.offset_ms ?? 0,
          total_duration_ms: all.reduce((s, c) => s + c.duration_ms, 0),
          total_bytes: all.reduce((s, c) => s + c.bytes, 0),
          total_chunks: all.length,
          has_more: hasMore,
          next_from: hasMore ? nextFrom : null,
          chunks: windowChunks.map((c) => ({
            chunk_index: c.chunk_index,
            session_idx: c.session_idx,
            offset_ms: c.offset_ms,
            duration_ms: c.duration_ms,
            bytes: c.bytes,
            r2_key: c.r2_key,
            url: mockEndpoints.signPlayback(c.r2_key, PLAYBACK_TTL_S).url,
          })),
        };
      }),
      photos: found.photos.map((p) => ({
        ...p,
        offset_ms: p.created_at - (found.attempt.started_at ?? p.created_at),
        url: mockEndpoints.signPlayback(p.r2_key, PLAYBACK_TTL_S).url,
      })),
    };
  },

  /**
   * Stores the override next to the auto score and recomputes the attempt
   * total, exactly as the endpoint does — `marks_awarded` is the effective
   * value, `auto_marks_awarded` is what the scorer originally produced.
   */
  overrideMarks(id: string, body: OverrideMarksInput): OverrideMarksResponse {
    const found = attemptStore.find((a) => a.attempt.id === id) ?? attemptStore[0];
    if (!found) throw new Error("No attempts");
    const answer = found.answers.find((a) => a.question.id === body.question_id);
    if (!answer) throw new Error(`Question ${body.question_id} is not on this attempt`);

    answer.marks_override = body.marks_awarded;
    answer.override_note = body.marks_awarded === null ? null : (body.note ?? null);
    answer.marks_awarded =
      body.marks_awarded === null ? answer.auto_marks_awarded : body.marks_awarded;

    const score =
      Math.round(found.answers.reduce((s, a) => s + (a.marks_awarded ?? 0), 0) * 100) /
      100;
    found.attempt.score = score;

    return {
      attempt: found.attempt,
      answer,
      score,
      max_score: found.attempt.max_score ?? 0,
    };
  },

  /**
   * Maps an R2 key onto one of the local fixture files. The real endpoint mints
   * an HMAC-signed Worker URL; only the URL shape matters to the player.
   */
  signPlayback(r2Key: string, expiresInS: number) {
    let url: string;
    const rec = /\/rec\/(webcam|phone)\/(\d+)\.webm$/.exec(r2Key);
    const photo = /\/photo\/([a-z_]+)\//.exec(r2Key);
    if (rec) {
      url = `${MEDIA_BASE}/${rec[1]}-${String(Number(rec[2]) % 8).padStart(2, "0")}.mp4`;
    } else if (photo) {
      // Stable per key so the gallery does not reshuffle between renders.
      let h = 0;
      for (let i = 0; i < r2Key.length; i++) h = (h * 31 + r2Key.charCodeAt(i)) >>> 0;
      url = `${MEDIA_BASE}/photo-${String(h % 7).padStart(2, "0")}.jpg`;
    } else {
      url = `${MEDIA_BASE}/missing`;
    }
    return { url, r2_key: r2Key, expires_at: Date.now() + expiresInS * 1000 };
  },
};

/** The first student of the fixture cohort, for screens that need a default. */
export const mockStudent = STUDENTS[0]!;
